import { useParams } from 'react-router';
import { useMatchByIdQuery } from '@data/matches/useMatchByIdQuery';
import { useMatchesQuery } from '@data/matches/useMatchesQuery';
import { useArticlesQuery } from '@data/articles/useArticlesQuery';
import { useLeagueTableQuery } from '@data/leagueTables/useLeagueTableQuery';
import { useIsMobile } from '../../utils/useIsMobile';
import { MatchSort, MatchStatusFilter } from '@repo/types';
import { MatchOverview } from '@ui/fixtures/components/MatchOverview';
import { Loader } from '@ui/common/components/Loader/Loader';
import { Helmet } from 'react-helmet-async';
import { NotFoundPage } from '../NotFoundPage';

// @ts-ignore fix vite import type
const isLeagueSite = import.meta.env.VITE_SITE_TYPE === 'league';

export const MatchPage = () => {
  const params = useParams();
  const isMobile = useIsMobile();

  const { data: match, isLoading: isMatchLoading, error: matchError } = useMatchByIdQuery(params.id);

  const { data: homeTeamArtices, isLoading: areHomeTeamArticlesLoading } = useArticlesQuery(
    {
      tags: isLeagueSite ? match?.homeTeam?.club?.slug : match?.homeTeam?.slug,
      limit: 3,
      endDate: match?.date,
    },
    {
      enabled: !!(isLeagueSite ? match?.homeTeam?.club?.slug : match?.homeTeam?.slug) && !!match?.date,
    }
  );

  const { data: homeTeamMatches, isLoading: areHomeTeamMatchesLoading } = useMatchesQuery(
    {
      limit: 5,
      sort: MatchSort.DESC,
      status: MatchStatusFilter.RESULT,
      club: match?.homeTeam?.externalId,
    },
    {
      enabled: !!match?.homeTeam?.slug,
    }
  );

  const { data: homeTeamLeagueTable, isLoading: isHomeTeamLeagueTableLoading } = useLeagueTableQuery(
    {
      externalTeamId: match?.homeTeam?.externalId,
    },
    {
      enabled: !!match?.homeTeam?.externalId,
    }
  );

  const { data: awayTeamArticles, isLoading: areAwayTeamArticlesLoading } = useArticlesQuery(
    {
      tags: isLeagueSite ? match?.awayTeam?.club?.slug : match?.awayTeam?.slug,
      limit: 3,
      endDate: match?.date,
    },
    {
      enabled: !!(isLeagueSite ? match?.awayTeam?.club?.slug : match?.awayTeam?.slug) && !!match?.date,
    }
  );

  const { data: awayTeamMatches, isLoading: areAwayTeamMatchesLoading } = useMatchesQuery(
    {
      limit: 5,
      sort: MatchSort.DESC,
      status: MatchStatusFilter.RESULT,
      club: match?.awayTeam?.externalId,
    },
    {
      enabled: !!match?.awayTeam?.slug,
    }
  );

  const { data: awayTeamLeagueTable, isLoading: isAwayTeamLeagueTableLoading } = useLeagueTableQuery(
    {
      externalTeamId: match?.awayTeam?.externalId,
    },
    {
      enabled: !!match?.awayTeam?.externalId,
    }
  );

  if (
    isMatchLoading ||
    areHomeTeamArticlesLoading ||
    areHomeTeamMatchesLoading ||
    isHomeTeamLeagueTableLoading ||
    areAwayTeamArticlesLoading ||
    areAwayTeamMatchesLoading ||
    isAwayTeamLeagueTableLoading
  ) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (!match || matchError) {
    return <NotFoundPage />;
  }

  const metaTitle = `${match.homeTeam.fullName} v ${match.awayTeam.fullName}, 2024/25 | ${
    import.meta.env.VITE_SITE_NAME
  }`;
  const metaDescription = `Read about ${match.homeTeam.fullName} v ${match.awayTeam.fullName} in the ${
    import.meta.env.VITE_ENTITY_NAME
  } 2024/25 season, including lineups, stats and live blogs, on the official website of the ${
    import.meta.env.VITE_SITE_NAME
  }.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <MatchOverview
        isMobile={isMobile}
        match={match}
        homeTeamArticles={homeTeamArtices}
        homeTeamMatches={homeTeamMatches}
        homeTeamLeagueTable={homeTeamLeagueTable}
        awayTeamArticles={awayTeamArticles}
        awayTeamMatches={awayTeamMatches}
        awayTeamLeagueTable={awayTeamLeagueTable}
      />
    </>
  );
};
